import React from 'react';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import config from '../config';

const DEFAULT_MAX_WIDTH = 260;

const formatBytes = (bytes) => {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** index;
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const ImageCard = ({
  image,
  width,
  onClick,
  disableOverlay = false,
  prefetchOriginal = false,
  height,
  containerSx,
  hidePrintfulStatus
}) => {
  const fallbackWidth = width && width > 0 ? width : DEFAULT_MAX_WIDTH;
  const ratio = image.width && image.height ? image.height / image.width : 0.75;
  const targetHeight = Math.max(Math.round(fallbackWidth * ratio), 160);
  const resolvedHeight =
    height != null ? (typeof height === 'number' ? `${height}px` : height) : `${targetHeight}px`;
  const resolveAssetUrl = React.useCallback((value) => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
    const normalized = value.startsWith('/') ? value : `/${value}`;
    try {
      const base = new URL(config.apiBaseUrl);
      return `${base.origin}${normalized}`;
    } catch {
      const fallbackBase = (config.apiBaseUrl || '').replace(/\/$/, '');
      return `${fallbackBase}${normalized}`;
    }
  }, []);

  const sources = React.useMemo(() => {
    const ordered = prefetchOriginal
      ? [image.image_url, image.original_url, image.upscaled_url, image.thumbnail_url]
      : [image.image_url, image.thumbnail_url, image.upscaled_url, image.original_url];

    ordered.push(`/v1/images/${image.id}/content`);

    const seen = new Set();
    const result = [];
    for (const value of ordered) {
      const resolved = resolveAssetUrl(value);
      if (!resolved || seen.has(resolved)) {
        continue;
      }
      seen.add(resolved);
      result.push(resolved);
    }
    return result;
  }, [
    image.id,
    image.image_url,
    image.original_url,
    image.thumbnail_url,
    image.upscaled_url,
    prefetchOriginal,
    resolveAssetUrl,
  ]);

  const [sourceIndex, setSourceIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setSourceIndex(0);
    setLoading(true);
  }, [sources]);

  const src = sources[sourceIndex] || '';

  const description = `${image.mime_type || 'unknown'} · ${image.width || 0}×${image.height || 0} · ${formatBytes(image.size)}`;

  const handleImageLoad = React.useCallback(() => {
    setLoading(false);
  }, []);

  const handleImageError = React.useCallback(() => {
    if (sourceIndex < sources.length - 1) {
      setSourceIndex((prev) => prev + 1);
      return;
    }
    setLoading(false);
  }, [sourceIndex, sources.length]);

  const renderPrintfulStatus = () => {
    if (hidePrintfulStatus) {
      return null;
    }

    if (image.printful_error) {
      return (
        <Tooltip title={image.printful_error} arrow placement="left">
          <Chip
            label="Printful: Error"
            color="error"
            size="small"
            sx={{ fontWeight: 600, pointerEvents: 'auto' }}
          />
        </Tooltip>
      );
    }
    if (image.sync_printful) {
      return (
        <Chip
          label="Printful: Synced"
          color="success"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      );
    }
    return (
      <Chip
        label="Printful: Pending"
        color="warning"
        sx={{
          fontWeight: 600,
          color: 'common.white',
          '& .MuiChip-label': {
            color: 'common.white',
          },
        }}
        size="small"
      />
    );
  };

  return (
    <Box
      sx={[
        {
          position: 'relative',
          width: '100%',
          height: resolvedHeight,
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.1s ease, box-shadow 0.1s ease',
          '&:hover': onClick
            ? {
                transform: 'translateY(-2px)',
                boxShadow: 6,
              }
            : undefined,
        },
        containerSx,
      ]}
      style={width ? { width: `${Math.round(width)}px` } : undefined}
      onClick={onClick}
      title={image.filename}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={image.path || image.id}
          loading="lazy"
          decoding="async"
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.15s ease',
          }}
        />
      ) : null}
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : null}
      {renderPrintfulStatus() ? (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {renderPrintfulStatus()}
        </Box>
      ) : null}
      {!disableOverlay ? (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: 1,
            background: 'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
          }}
        >
          <Typography variant="caption" color="common.white">
            {description}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default ImageCard;
